import { uniqueId } from 'lodash'
import { ForwardedRef, forwardRef, SyntheticEvent, useContext, useEffect, useRef, useState } from 'react'
import { Position } from 'types'
import { PortalTooltip, useTooltip } from 'ui'
import Artboard, { ArtboardImage, ArtboardText } from '../../artboard'
import { ArtboardRef } from '../../artboard/types/refs'
import LayerContextMenu from '../components/layer-context-menu'
import { UgeArtboardContext } from '../contexts/artboard'
import { UgeEditorContext } from '../contexts/editor'
import { UgeLayersContext } from '../contexts/layers'
import { CaptionLayerProps, ImageLayerProps, Layer } from '../types'

type Props = {
    crop?: Position
    disabled?: boolean
    paused?: boolean
}

const Board = ({ crop, disabled = false, paused: pausedProp = false }: Props, ref: ForwardedRef<ArtboardRef>) => {
    const tooltipIdRef = useRef<string>(uniqueId('uge-layer-tooltip-'))
    const { fps, height, paused, source, timing, width, setDuration, setTiming } = useContext(UgeArtboardContext)
    const { activeLayerId, layers } = useContext(UgeLayersContext)
    const { openEditor } = useContext(UgeEditorContext)
    const [tooltipLayer, setTooltipLayer] = useState<Layer | undefined>()

    const { closeTooltip, listeners, isOpen } = useTooltip({
        color: '#111111',
        direction: 'right',
        id: tooltipIdRef.current,
        triggerType: 'all',
        width: 226,
    })

    const onContextMenu = (e: SyntheticEvent<HTMLElement, MouseEvent>, layer: Layer) => {
        setTooltipLayer(layer)
        return listeners.onContextMenu?.(e)
    }

    const onEllipsisMenu = (e: SyntheticEvent<HTMLElement, MouseEvent>, layer: Layer) => {
        setTooltipLayer(layer)
        return listeners.onClick?.(e)
    }

    const onDurationChange = (duration: number) => {
        if (timing) {
            setTiming(
                duration
                    ? {
                          start: Math.min(timing.start, duration),
                          end: Math.min(timing.end, duration),
                      }
                    : undefined
            )
        }

        setDuration(duration)
    }

    const onTextLayerDoubleClick = (layer: Layer) => {
        openEditor('captions', { layerId: layer.id })
    }

    useEffect(() => {
        if (!isOpen) {
            setTooltipLayer(undefined)
        }
    }, [isOpen])

    return (
        <>
            <Artboard
                crop={crop}
                disabled={disabled}
                fps={fps}
                height={height}
                paused={paused || pausedProp}
                ref={ref}
                timing={timing}
                width={width}
                onDurationChange={onDurationChange}
            >
                <ArtboardImage
                    defaultPosition={{
                        height: height,
                        rotation: 0,
                        width: width,
                        x: Math.round(width / 2),
                        y: Math.round(height / 2),
                    }}
                    source={source}
                    disabled
                />
                {layers.map((layer) => {
                    const { id, props, type } = layer
                    switch (type) {
                        case 'caption':
                            return (props as CaptionLayerProps).text ? (
                                <ArtboardText
                                    {...(props as CaptionLayerProps)}
                                    key={id}
                                    highlighted={id === activeLayerId}
                                    onContextMenu={(e) => onContextMenu(e, layer)}
                                    onDoubleClick={() => onTextLayerDoubleClick(layer)}
                                    onEllipsisMenu={(e) => onEllipsisMenu(e, layer)}
                                />
                            ) : null
                        case 'image':
                            return (
                                <ArtboardImage
                                    {...(props as ImageLayerProps)}
                                    key={id}
                                    highlighted={id === activeLayerId}
                                    onContextMenu={(e) => onContextMenu(e, layer)}
                                    onEllipsisMenu={(e) => onEllipsisMenu(e, layer)}
                                />
                            )
                    }
                })}
            </Artboard>
            <PortalTooltip isOpen={isOpen}>
                {!!tooltipLayer && <LayerContextMenu layer={tooltipLayer} onAfterAction={closeTooltip} />}
            </PortalTooltip>
        </>
    )
}

export default forwardRef(Board)
